<template>
  <div class="min-h-screen bg-background text-text">
    <div class="container mx-auto px-4 py-8 md:py-10">
      <div class="mb-8">
        <p class="text-xs uppercase tracking-[0.22em] text-gold font-semibold">Communauté</p>
        <h1 class="mt-2 text-3xl md:text-5xl font-bold text-primary">Salons</h1>
        <p class="mt-3 text-secondary max-w-2xl">
          Échangez avec les autres profils de votre zone, selon votre niveau de classement et votre territoire.
        </p>
      </div>

      <div class="mb-6 flex flex-wrap gap-2">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          @click="selectTab(tab.id)"
          :class="[
            'rounded-full px-4 py-2 text-sm font-semibold border transition-colors',
            activeTab === tab.id
              ? 'bg-gold text-primary border-gold'
              : 'bg-transparent text-secondary border-secondary/30 hover:border-gold hover:text-gold'
          ]"
        >
          {{ tab.label }}
        </button>
      </div>

      <div v-if="loading" class="rounded-2xl border border-gold/20 bg-backgroundLight p-6 text-secondary">
        Chargement des salons...
      </div>

      <div v-else class="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside class="rounded-2xl border border-gold/20 bg-backgroundLight p-4">
          <div class="mb-4 flex items-center justify-between">
            <h2 class="text-lg font-bold text-primary">Salons</h2>
            <span class="rounded-full bg-gold/10 text-gold px-2 py-1 text-xs font-semibold">{{ filteredRooms.length }} actifs</span>
          </div>

          <div class="space-y-3">
            <button
              v-for="room in filteredRooms"
              :key="room.id"
              type="button"
              @click="selectRoom(room)"
              :class="[
                'w-full rounded-xl border p-3 text-left transition-colors',
                selectedRoom?.id === room.id
                  ? 'border-gold bg-gold/10'
                  : 'border-secondary/15 bg-background hover:border-gold/40'
              ]"
            >
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="text-[10px] uppercase tracking-[0.15em] text-gold">{{ room.scopeLabel || room.scope }}</p>
                  <h3 class="mt-1 font-bold text-primary">{{ room.name }}</h3>
                </div>
                <span class="rounded-full bg-background px-2 py-1 text-[10px] font-semibold text-gold">
                  {{ room.online || 0 }} en ligne
                </span>
              </div>
              <p class="mt-2 text-sm text-secondary">{{ room.topic }}</p>
            </button>
          </div>
        </aside>

        <section class="rounded-2xl border border-gold/20 bg-backgroundLight overflow-hidden">
          <div class="flex items-center justify-between border-b border-gold/20 px-5 py-4">
            <div>
              <p class="text-[10px] uppercase tracking-[0.15em] text-gold">{{ selectedRoom?.scopeLabel || selectedRoom?.scope || 'Salon' }}</p>
              <h2 class="text-xl font-bold text-primary">{{ selectedRoom?.name || 'Salon' }}</h2>
            </div>
            <div class="text-sm text-secondary">{{ selectedRoom?.online || 0 }} connectés</div>
          </div>

          <div class="h-[420px] overflow-y-auto p-4 space-y-3 bg-background/60">
            <div v-if="!messages.length" class="text-sm text-secondary italic">
              Aucun message pour le moment. Soyez le premier à écrire.
            </div>

            <div
              v-for="message in messages"
              :key="message.id"
              class="max-w-[80%] rounded-2xl px-4 py-2 text-sm"
              :class="message.userId === currentUserId ? 'ml-auto bg-gold text-primary' : 'bg-backgroundLight border border-secondary/15 text-text'"
            >
              <div class="mb-1 flex items-center justify-between gap-4 text-[10px] uppercase tracking-[0.12em] opacity-75">
                <span>{{ message.username }}</span>
                <span>{{ formatTime(message.createdAt) }}</span>
              </div>
              <p>{{ message.content }}</p>

              <div v-if="message.userId !== currentUserId && isConnected" class="mt-2 flex gap-2">
                <button
                  type="button"
                  @click="reportMessage(message)"
                  class="text-[10px] uppercase tracking-[0.12em] text-secondary hover:text-gold transition-colors"
                >
                  Signaler
                </button>
                <button
                  type="button"
                  @click="blockUser(message.userId)"
                  class="text-[10px] uppercase tracking-[0.12em] text-secondary hover:text-red-400 transition-colors"
                >
                  Bloquer
                </button>
              </div>
            </div>
          </div>

          <div class="border-t border-gold/20 p-4">
            <div v-if="!isConnected" class="flex items-center justify-between gap-3 rounded-xl border border-gold/20 bg-background p-3 text-sm text-secondary">
              <span>Connectez-vous pour participer au salon.</span>
              <router-link to="/login-register" class="btn-gold px-3 py-2 text-xs">Se connecter</router-link>
            </div>

            <div v-else class="flex gap-3">
              <input
                v-model="draft"
                type="text"
                placeholder="Écrire un message..."
                class="flex-1 rounded-full border border-secondary/20 bg-background px-4 py-3 text-sm text-text placeholder:text-secondary focus:outline-none focus:border-gold"
                @keyup.enter="sendMessage"
              />
              <button
                type="button"
                @click="sendMessage"
                class="btn-gold whitespace-nowrap"
              >
                Envoyer
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import ChatService from '@/services/chat.service'
import { useAuthStore } from '@/stores/authStore'

const authStore = useAuthStore()
const tabs = [
  { id: 'all', label: 'Tous' },
  { id: 'region', label: 'Région' },
  { id: 'department', label: 'Département' },
  { id: 'city', label: 'Ville' },
  { id: 'national', label: 'National' }
]

const rooms = ref([])
const loading = ref(true)
const activeTab = ref('all')
const selectedRoom = ref(null)
const messages = ref([])
const draft = ref('')
const currentUserId = computed(() => authStore.user?.id || null)
const isConnected = computed(() => authStore.isLoggedIn || authStore.isAuthenticated)
let pollTimer = null

const filteredRooms = computed(() => {
  if (!rooms.value.length) return []
  if (activeTab.value === 'all') return rooms.value

  if (activeTab.value === 'national') {
    return rooms.value.filter((room) => room.scope === 'national')
  }

  if (activeTab.value === 'region') {
    return rooms.value.filter((room) => room.scope === 'region')
  }

  if (activeTab.value === 'department') {
    return rooms.value.filter((room) => room.scope === 'department')
  }

  if (activeTab.value === 'city') {
    return rooms.value.filter((room) => room.scope === 'city')
  }

  return rooms.value
})

function selectTab(tabId) {
  activeTab.value = tabId
  const nextRoom = filteredRooms.value[0]
  if (nextRoom) {
    selectRoom(nextRoom)
  }
}

async function loadRooms() {
  try {
    loading.value = true
    const data = await ChatService.getRooms()
    rooms.value = data
    if (!data.length) {
      selectedRoom.value = null
      messages.value = []
      return
    }

    const firstRoom = filteredRooms.value[0]
    if (firstRoom) {
      await selectRoom(firstRoom)
    }
  } catch (error) {
    console.error('Erreur chargement salons', error)
  } finally {
    loading.value = false
  }
}

async function selectRoom(room) {
  selectedRoom.value = room
  if (!room?.id) return

  try {
    if (isConnected.value) {
      await ChatService.joinRoom(room.id)
    }

    const roomMessages = await ChatService.getMessages(room.id)
    messages.value = roomMessages || []
  } catch (error) {
    console.error('Erreur chargement messages', error)
    messages.value = []
  }
}

async function sendMessage() {
  if (!isConnected.value) return
  if (!draft.value.trim() || !selectedRoom.value?.id) return

  try {
    const payload = await ChatService.postMessage(selectedRoom.value.id, draft.value)
    messages.value.push(payload)
    draft.value = ''
    await refreshRoomList()
    await refreshMessages()
  } catch (error) {
    console.error('Erreur envoi message', error)
  }
}

async function refreshMessages() {
  if (!selectedRoom.value?.id) return

  try {
    const roomMessages = await ChatService.getMessages(selectedRoom.value.id)
    messages.value = roomMessages || []
  } catch (error) {
    console.error('Erreur refresh messages', error)
  }
}

async function refreshRoomList() {
  try {
    const data = await ChatService.getRooms()
    rooms.value = data || []

    if (!selectedRoom.value?.id) {
      const firstRoom = filteredRooms.value[0]
      if (firstRoom) {
        await selectRoom(firstRoom)
      }
      return
    }

    const updatedSelected = rooms.value.find((room) => room.id === selectedRoom.value.id)
    if (updatedSelected) {
      selectedRoom.value = updatedSelected
    }
  } catch (error) {
    console.error('Erreur refresh salons', error)
  }
}

async function reportMessage(message) {
  if (!message?.id || !isConnected.value) return

  try {
    await ChatService.reportMessage(message.id, 'spam')
    console.info('Message signalé', message.id)
  } catch (error) {
    console.error('Erreur signalement message', error)
  }
}

async function blockUser(userId) {
  if (!userId || !isConnected.value) return

  try {
    await ChatService.blockUser(userId, 'manual')
    console.info('Utilisateur bloqué', userId)
  } catch (error) {
    console.error('Erreur blocage utilisateur', error)
  }
}

function formatTime(value) {
  if (!value) return ''
  const date = new Date(value)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

onMounted(() => {
  loadRooms()
  pollTimer = setInterval(() => {
    refreshRoomList()
    refreshMessages()
  }, 15000)
})

onBeforeUnmount(() => {
  if (pollTimer) {
    clearInterval(pollTimer)
  }
})
</script>
